import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { selectors } from 'darwin_core';

import BackLink from '../navigation/BackLink.jsx';
import { homeActionPath } from '../navigation/paths.js';
import { useHomeRouteContext } from '../navigation/HomeRoute.jsx';
import {
  IDEAL_SUPPORT_DISPLAY,
  IDEAL_SUPPORT_NUMBER,
  alarmId,
  alarmResolutionText,
  alarmTitle,
  installerIsNotIdeal,
  sameFaults,
} from '../data/alarmModel.js';
import useScreen from '../hooks/useScreen.js';

// A single active alarm's detail — the app's AlarmDetails content (title,
// resolution guidance, contact numbers), on its own deep-linkable route
// (/property/:homeId/data/alarm/:alarmKey). The alarm is re-found in the live fault
// list by the same composite key the Data list linked with (raisedAt + fault
// code/type — raisedAt alone is not unique); if it has since cleared (or the link
// is stale) it shows a "no longer active" message, mirroring the app's resolved
// handling without needing the pushed alarm payload.

const telHref = (number) => `tel:${String(number).split(' ').join('')}`;

export default function AlarmDetailScreen() {
  const { homeId } = useHomeRouteContext();
  // Named alarmKey to avoid shadowing the alarmId() key builder from alarmModel.
  const { alarmId: alarmKey } = useParams();

  const faults = useSelector(
    (s) => selectors.getAlarmsBySeverity(s, homeId),
    sameFaults,
  );
  const installerNumber = useSelector((s) =>
    selectors.getInstallerContactNumber(s, homeId),
  );

  const fault = faults.find((f) => alarmId(f) === alarmKey);
  const back = homeActionPath(homeId, 'data');
  // Called before the early return so hook order is stable across both branches.
  const headingRef = useScreen(fault ? alarmTitle(fault) : 'Alarm');

  if (!fault) {
    return (
      <main className="app-shell screen alarm-screen">
        <header className="screen__header">
          <BackLink to={back} label="Data" />
          <h1 id="main-content" tabIndex={-1} ref={headingRef}>
            Alarm
          </h1>
        </header>
        <p className="dashboard-status" role="status">
          This alarm is no longer active.
        </p>
      </main>
    );
  }

  const isHaloServiceDue = fault.type === 'HaloServiceDue';
  const severity = fault.severity
    ? String(fault.severity).toLowerCase()
    : 'information';
  const showEngineer = installerIsNotIdeal(installerNumber);

  return (
    <main className="app-shell screen alarm-screen">
      <header className="screen__header">
        <BackLink to={back} label="Data" />
        <h1 id="main-content" tabIndex={-1} ref={headingRef}>
          {alarmTitle(fault)}
        </h1>
        {fault.severity ? (
          <p className={`alarm-screen__sev alarm-screen__sev--${severity}`}>
            {fault.severity}
          </p>
        ) : null}
      </header>

      <section className="alarm-screen__body">
        <p className="alarm-screen__resolution">
          {alarmResolutionText(fault, installerNumber)}
        </p>

        {(showEngineer || !isHaloServiceDue) && (
          <div className="alarm-screen__contacts">
            {showEngineer && (
              <a
                className="alarm-screen__contact"
                href={telHref(installerNumber)}
              >
                <span className="alarm-screen__contact-label">
                  Service Engineer
                </span>
                <span className="alarm-screen__contact-number">
                  {installerNumber}
                </span>
              </a>
            )}
            {!isHaloServiceDue && (
              <a
                className="alarm-screen__contact"
                href={telHref(IDEAL_SUPPORT_NUMBER)}
              >
                <span className="alarm-screen__contact-label">
                  Ideal Customer Support
                </span>
                <span className="alarm-screen__contact-number">
                  {IDEAL_SUPPORT_DISPLAY}
                </span>
              </a>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
